import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatRippleModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  CurrentScopeFacade,
  EmptyStateComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  CustomTooltipModule,
  InputSearchFilter,
  NgCommonModule,
  toSearchStream,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { filterUndefined, snapshotDefined } from '@principle-theorem/shared';
import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CampaignEnrolmentSummaryComponent } from '../campaign-enrolment-summary/campaign-enrolment-summary.component';
import { CampaignEntrySummaryComponent } from '../campaign-entry-summary/campaign-entry-summary.component';
import { CampaignReportSummaryComponent } from '../campaign-report-summary/campaign-report-summary.component';
import {
  IMarketingCampaignStepSummary,
  MarketingCampaignStepSummary,
} from '../campaign-steps-summary/campaign-step-summary';
import { FileUploadSummaryComponent } from '../file-upload-summary/file-upload-summary.component';
import { IMarketingCampaignTemplate } from '../marketing-campaign-templates/marketing-campaign-template';
import {
  getDefaultMarketingCampaignTemplate,
  getMarketingCampaignTemplates,
} from '../marketing-campaign-templates/marketing-campaign-templates';

@Component({
  selector: 'pr-campaign-template-selector',
  templateUrl: './campaign-template-selector.component.html',
  styleUrl: './campaign-template-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatRippleModule,
    EmptyStateComponent,
    CustomTooltipModule,
    CampaignEntrySummaryComponent,
    CampaignEnrolmentSummaryComponent,
    CampaignReportSummaryComponent,
    FileUploadSummaryComponent,
  ],
  host: { class: 'flex flex-col gap-4' },
})
export class CampaignTemplateSelectorComponent {
  private _scope = inject(CurrentScopeFacade);
  private _organisation = inject(OrganisationService);
  searchCtrl = new TypedFormControl('');
  selectedTemplate = signal<IMarketingCampaignTemplate | undefined>(undefined);
  templateSelected = output<IMarketingCampaignTemplate>();
  searchFilter: InputSearchFilter<IMarketingCampaignTemplate>;
  tooltipPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
    },
  ];

  constructor() {
    const templates$ = combineLatest([
      this._organisation.region$,
      this._scope.currentPractice$,
    ]).pipe(
      map(([region, practice]) =>
        practice && region
          ? getMarketingCampaignTemplates(practice.ref, region)
          : []
      )
    );
    combineLatest([this._organisation.region$, this._scope.currentPractice$])
      .pipe(
        map(([region, practice]) =>
          practice && region
            ? getDefaultMarketingCampaignTemplate(practice.ref, region)
            : undefined
        ),
        filterUndefined(),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe((defaultTemplate) => this.selectTemplate(defaultTemplate));

    this.searchFilter = new InputSearchFilter<IMarketingCampaignTemplate>(
      templates$,
      toSearchStream(this.searchCtrl),
      ['name', 'description']
    );
  }

  selectTemplate(template: IMarketingCampaignTemplate): void {
    this.selectedTemplate.set(template);
    this.templateSelected.emit(template);
  }

  async getStepSummary(
    template: IMarketingCampaignTemplate
  ): Promise<IMarketingCampaignStepSummary> {
    const brand = await snapshotDefined(this._scope.currentBrand$);
    return MarketingCampaignStepSummary.getSummary(brand.ref, template.steps);
  }
}
